import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { LlmLastError, LlmManager, WebLlmLoadingProgress } from './LlmManager';

export interface AgentSuggestion {
  id?: string;
  kind?: 'answer' | 'action';
  content: string;
  onAccept: (content: string) => Promise<unknown> | unknown;
  prompt?: string;
  appId?: string;
  timestamp?: number;
}

export interface AiSessionMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  appId?: string;
  timestamp: number;
  onAccept?: (content: string) => Promise<unknown> | unknown;
  status?: 'pending' | 'running' | 'success' | 'error' | 'cancelled';
  kind?: 'answer' | 'action' | 'receipt';
}

interface LlmAgentContextValue {
  isLoading: boolean;
  streamingContent: string;
  /** Local model download / GPU initialization progress for the active request. */
  webLlmLoading: WebLlmLoadingProgress | null;
  lastSuggestion: AgentSuggestion | null;
  /** The last LLM error, if any — cleared when a new request starts or succeeds. */
  lastError: LlmLastError | null;
  /** Full session conversation turns */
  sessionMessages: AiSessionMessage[];
  /** Whether the full session conversation popup / drawer is open */
  isConvoOpen: boolean;
  setIsConvoOpen: (open: boolean) => void;
  /** Whether interactive AI conversation mode is currently active in the status/input bar */
  inConversationMode: boolean;
  setInConversationMode: (active: boolean) => void;
  startConversation(): void;
  endConversation(): void;
  dismiss(): void;
  accept(content: string, suggestionId?: string): Promise<void>;
  clearSession(): void;
  recordTurn(userPrompt: string, assistantResponse: string, appId?: string, onAccept?: (content: string) => Promise<unknown> | unknown): void;
}

const LlmAgentContext = createContext<LlmAgentContextValue>({
  isLoading: false,
  streamingContent: '',
  webLlmLoading: null,
  lastSuggestion: null,
  lastError: null,
  sessionMessages: [],
  isConvoOpen: false,
  setIsConvoOpen: () => {},
  inConversationMode: false,
  setInConversationMode: () => {},
  startConversation: () => {},
  endConversation: () => {},
  dismiss: () => {},
  accept: async () => {},
  clearSession: () => {},
  recordTurn: () => {},
});

interface Props {
  llmManager: LlmManager;
  eventBus?: {
    emit: (event: string, payload?: unknown) => void;
    on?: (event: string, callback: (payload: any) => void) => () => void;
  };
  children: React.ReactNode;
}

export function LlmAgentProvider({ llmManager, eventBus, children }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [webLlmLoading, setWebLlmLoading] = useState<WebLlmLoadingProgress | null>(
    () => llmManager.getWebLlmLoadingState(),
  );
  const [lastSuggestion, setLastSuggestion] = useState<AgentSuggestion | null>(null);
  const [lastError, setLastError] = useState<LlmLastError | null>(null);
  const [sessionMessages, setSessionMessages] = useState<AiSessionMessage[]>([]);
  const [isConvoOpen, setIsConvoOpen] = useState(false);
  const [inConversationMode, setInConversationMode] = useState(false);
  const currentPromptRef = React.useRef<{ prompt?: string; appId?: string }>({});
  const lastSuggestionRef = React.useRef<AgentSuggestion | null>(null);

  useEffect(() => {
    lastSuggestionRef.current = lastSuggestion;
    eventBus?.emit('ai:suggestion', lastSuggestion ? {
      content: lastSuggestion.content,
      kind: lastSuggestion.kind,
      id: lastSuggestion.id,
      prompt: lastSuggestion.prompt,
      appId: lastSuggestion.appId,
      timestamp: lastSuggestion.timestamp,
    } : null);
  }, [lastSuggestion, eventBus]);

  useEffect(() => {
    llmManager.setAgentCallbacks(
      (appId, prompt) => {
        setIsLoading(true);
        setStreamingContent('');
        setLastSuggestion(null);
        lastSuggestionRef.current = null;
        setLastError(null); // clear previous error on new request
        if (prompt) {
          currentPromptRef.current = { appId, prompt };
          setSessionMessages(previous => previous.map(msg => msg.status === 'pending' ? { ...msg, status: 'cancelled', onAccept: undefined } : msg));
        }
        if (prompt) {
          const userMsg: AiSessionMessage = {
            id: `usr-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            role: 'user',
            content: prompt,
            appId,
            timestamp: Date.now(),
          };
          setSessionMessages(prev => [...prev, userMsg]);
        }
      },
      (suggestion) => {
        setIsLoading(false);
        setStreamingContent('');
        if (suggestion) {
          const id = `asst-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          const enrichedSuggestion: AgentSuggestion = {
            id,
            kind: suggestion.kind ?? 'answer',
            content: suggestion.content,
            onAccept: suggestion.onAccept,
            prompt: suggestion.prompt ?? currentPromptRef.current.prompt,
            appId: suggestion.appId ?? currentPromptRef.current.appId,
            timestamp: Date.now(),
          };
          setLastSuggestion(enrichedSuggestion);
          lastSuggestionRef.current = enrichedSuggestion;
          const assistantMsg: AiSessionMessage = {
            id,
            role: 'assistant',
            content: suggestion.content,
            appId: enrichedSuggestion.appId,
            timestamp: Date.now(),
            onAccept: suggestion.onAccept,
            kind: enrichedSuggestion.kind,
            status: enrichedSuggestion.kind === 'action' ? 'pending' : undefined,
          };
          setSessionMessages(prev => [...prev, assistantMsg]);
        } else {
          setLastSuggestion(null);
          lastSuggestionRef.current = null;
        }
      },
      (chunk) => setStreamingContent(previous => previous + chunk),
      (tool, result) => {
        setSessionMessages(previous => [...previous.map(msg => msg.status === 'running' ? { ...msg, status: result.success ? 'success' as const : 'error' as const, onAccept: undefined } : msg), {
          id: `receipt-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          role: 'system' as const, kind: 'receipt' as const,
          content: `[TOOL RESULT for ${tool}]\n${JSON.stringify(result)}`,
          appId: currentPromptRef.current.appId, timestamp: Date.now(),
          status: result.success ? 'success' as const : 'error' as const,
        }]);
      },
    );
  }, [llmManager]);

  useEffect(() => llmManager.onLoadingChange(setWebLlmLoading), [llmManager]);

  // Subscribe to LLM error events from the manager
  useEffect(() => {
    return llmManager.onErrorChange((err) => {
      setLastError(err);
      // If an error occurs while we thought we were loading, update loading state
      if (err) {
        setIsLoading(false);
        const errorMsg: AiSessionMessage = {
          id: `err-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          role: 'system',
          content: err.message || 'AI request failed.',
          appId: currentPromptRef.current.appId,
          timestamp: Date.now(),
          status: 'error',
        };
        setSessionMessages(prev => [...prev, errorMsg]);
      }
    });
  }, [llmManager]);

  const startConversation = useCallback(() => {
    setInConversationMode(true);
  }, []);

  const endConversation = useCallback(() => {
    setInConversationMode(false);
  }, []);

  const dismiss = useCallback(() => {
    llmManager.cancelAgent();
    setSessionMessages(previous => previous.map(msg => msg.status === 'pending' ? { ...msg, status: 'cancelled', onAccept: undefined } : msg));
    setLastSuggestion(null);
    lastSuggestionRef.current = null;
    setLastError(null);
    setIsLoading(false);
  }, [llmManager]);

  const accept = useCallback(async (content: string, suggestionId?: string) => {
    const suggestion = lastSuggestionRef.current;
    if (!suggestion || suggestion.kind !== 'action' || suggestion.content !== content || (suggestionId !== undefined && suggestion.id !== suggestionId)) return;
    // Consume before awaiting to prevent double clicks and historical-action replay.
    lastSuggestionRef.current = null;
    setLastSuggestion(null);
    setSessionMessages(previous => previous.map(msg => msg.id === suggestion.id ? { ...msg, status: 'running', onAccept: undefined } : msg));
    try {
      const res: any = await suggestion.onAccept(content);
      if (res?.message) eventBus?.emit('toast', res.message);
    } catch (err: any) {
      setSessionMessages(previous => previous.map(msg => msg.id === suggestion.id ? { ...msg, status: 'error' } : msg));
      eventBus?.emit('toast', err?.message || 'Action failed');
      throw err;
    }
  }, [eventBus]);

  useEffect(() => {
    if (!eventBus?.on) return;
    const offAccept = eventBus.on('ai:accept', () => {
      const suggestion = lastSuggestionRef.current;
      if (suggestion) void accept(suggestion.content).catch(() => {});
    });
    const offDismiss = eventBus.on('ai:dismiss', dismiss);
    return () => { offAccept(); offDismiss(); };
  }, [eventBus, dismiss, accept]);

  const clearSession = useCallback(() => {
    llmManager.cancelAgent();
    lastSuggestionRef.current = null;
    setIsLoading(false);
    setSessionMessages([]);
    setLastSuggestion(null);
    setLastError(null);
    setInConversationMode(false);
  }, [llmManager]);

  const recordTurn = useCallback((userPrompt: string, assistantResponse: string, appId?: string, onAccept?: (content: string) => void) => {
    const now = Date.now();
    const uMsg: AiSessionMessage = {
      id: `usr-${now}-${Math.random().toString(36).slice(2, 6)}`,
      role: 'user',
      content: userPrompt,
      appId,
      timestamp: now,
    };
    const aMsg: AiSessionMessage = {
      id: `asst-${now + 1}-${Math.random().toString(36).slice(2, 6)}`,
      role: 'assistant',
      content: assistantResponse,
      appId,
      timestamp: now + 1,
      onAccept,
      status: 'success',
    };
    setSessionMessages(prev => [...prev, uMsg, aMsg]);
    setLastSuggestion({
      content: assistantResponse,
      onAccept: onAccept || (() => {}),
      prompt: userPrompt,
      appId,
      timestamp: now + 1,
    });
  }, []);

  return (
    <LlmAgentContext.Provider value={{
      isLoading,
      streamingContent,
      webLlmLoading,
      lastSuggestion,
      lastError,
      sessionMessages,
      isConvoOpen,
      setIsConvoOpen,
      inConversationMode,
      setInConversationMode,
      startConversation,
      endConversation,
      dismiss,
      accept,
      clearSession,
      recordTurn,
    }}>
      {children}
    </LlmAgentContext.Provider>
  );
}

export function useLlmAgent(): LlmAgentContextValue {
  return useContext(LlmAgentContext);
}
